import React from 'react';

import {
  BOUserStatusEnum,
  CustomerStatusEnum,
  AssignmentStageEnum,
  StageStatusEnum,
  AssignmentStageModel,
  CustomerStageModel,
  BOUsersStageModel,
} from 'api/adminApiClient';
import { StepValidationStatuses, UserRole } from 'enums';

export const DEFAULT_ACTION_BTN_TEXT = 'Done';
export const DEFAULT_ACTION_MESSAGE = 'Stage confirmation';
export const DEFAULT_STAGE_COMPLETED_MESSAGE = 'Stage confirmed';
export const DEFAULT_ACTION_FORBIDDEN_MESSAGE =
  'You need to complete previous steps in order to complete this one';
const PREVIOUS_STAGE_NOT_COMPLTED_WARNING =
  'Please note: there are incomplete stages prior to this one.';

export const MEMBER_SOFT_STOP_MESSAGES = {
  ADMIN_RESPONSIBLE:
    'Soft stop: please check your email and reply to it with the requested information. Soft stop still allows us to complete this stage.',
  MEMBER_RESPONSIBLE:
    'Soft stop: please check your email and reply to it with the requested information. Soft stop still allows you to complete this stage.',
};

export const ADMIN_SOFT_STOP_MESSAGES = {
  ADMIN_RESPONSIBLE:
    'Soft stop: additional information requested. Although needed, it will not block us from completing this stage.',
  MEMBER_RESPONSIBLE:
    'Soft stop: additional information requested. Although needed, it will not block member from completing this stage.',
};

export const SOFT_HALF_STOP_MESSAGES = {
  ADMIN: (
    <>
      {' '}
      Stage was completed, but additional information requested was not
      received. <br /> Have you received it?{' '}
    </>
  ),
  MEMBER:
    "Stage was completed, but we are waiting for admin to confirm that additional information was received. If you haven'\t sent us requested information yet, please do so as soon as possible.",
};

export const MEMBER_HARD_SOFT_STOP_MESSAGES = {
  ADMIN_RESPONSIBLE:
    'Hard stop: please check your email and reply to it with the requested information. We are not able to complete this stage until information is provided.',
  MEMBER_RESPONSIBLE:
    'Hard stop: please check your email and reply to it with the requested information. You are not able to complete this stage until information is provided.',
};

export const ADMIN_HARD_SOFT_STOP_MESSAGES = {
  ADMIN_RESPONSIBLE:
    'Hard stop: additional information requested. We are not able to complete this stage until information is provided.',
  MEMBER_RESPONSIBLE:
    'Hard stop: additional information requested. Member is not able to complete this stage until information is provided.',
};

export type ProfileStatus =
  | BOUserStatusEnum
  | CustomerStatusEnum
  | AssignmentStageEnum;

type ProfileStageModel =
  | BOUsersStageModel
  | CustomerStageModel
  | AssignmentStageModel;

export interface StepChangeRequestModel {
  message?: string;
  stage: ProfileStatus;
  stageStatus: StageStatusEnum;
}

export interface AdditionalInfoResponseModel {
  responseMessage: string;
  stage: ProfileStatus;
}

export interface ProgressStepConfigParams {
  arePrevStagesCompleted: boolean;
}

export interface StageMessages {
  softStopMessage: string | React.ReactNode;
  halfSoftStopMessage: string | React.ReactNode;
  hardStopMessage: string | React.ReactNode;
  stageCompletedMessage?: string | React.ReactNode;
  pendingMessage?: string | React.ReactNode;
}

export interface GeneralProgressStep {
  errorText?: string | React.ReactNode;
  actionBtnText?: string;
  isActionEnabled?: boolean;
  displayRequestButton?: boolean;
  displayResponseButton?: boolean;
  requestMessage?: string;
  responseMessage?: string;
  isPulsating?: boolean;
}

export interface ProgressStepOptions extends GeneralProgressStep {
  messages: StageMessages;
  responsibleRole?: UserRole.Admin | UserRole.Users;
}

export interface ProfileProgressStep<T> extends GeneralProgressStep {
  stage: T;
  message: string;
  isCompleted: boolean;
  stageStatus: StageStatusEnum;
  stepValidationStatus: StepValidationStatuses;
  responsibleRole: UserRole.Admin | UserRole.Users;
}

interface StageMessageOptions extends StageMessages {
  isAdmin?: boolean;
  arePrevStagesCompleted?: boolean;
}
export const getStageMessage = (
  stage: StageStatusEnum,
  options: StageMessageOptions,
) => {
  const {
    isAdmin,
    arePrevStagesCompleted,
    softStopMessage,
    hardStopMessage,
    halfSoftStopMessage,
    stageCompletedMessage,
    pendingMessage,
  } = options;

  switch (stage) {
    case StageStatusEnum.SoftStop:
      return softStopMessage;

    case StageStatusEnum.HardStop:
      return hardStopMessage;

    case StageStatusEnum.SoftStopHalfCompleted:
      return halfSoftStopMessage;

    case StageStatusEnum.Completed:
      return stageCompletedMessage || DEFAULT_STAGE_COMPLETED_MESSAGE;

    case StageStatusEnum.Pending:
      return !arePrevStagesCompleted && isAdmin
        ? `${
            pendingMessage || DEFAULT_ACTION_MESSAGE
          }. ${PREVIOUS_STAGE_NOT_COMPLTED_WARNING}`
        : pendingMessage || DEFAULT_ACTION_MESSAGE;
  }
};

export const isSoftStopHalfCompleted = (stageStatus?: StageStatusEnum) => {
  return stageStatus === StageStatusEnum.SoftStopHalfCompleted;
};

export const isSoftStopStage = (stageStatus?: StageStatusEnum) => {
  return stageStatus === StageStatusEnum.SoftStop;
};

export const isHardStopStage = (stageStatus?: StageStatusEnum) => {
  return stageStatus === StageStatusEnum.HardStop;
};

export const isSoftHardStopStage = (stageStatus?: StageStatusEnum) => {
  return isHardStopStage(stageStatus) || isSoftStopStage(stageStatus);
};

export const isStageCompleted = (stageStatus?: StageStatusEnum) => {
  return stageStatus === StageStatusEnum.Completed;
};

export const isMarkedAsStopStage = (stageStatus?: StageStatusEnum) => {
  return (
    isSoftStopHalfCompleted(stageStatus) ||
    isSoftStopStage(stageStatus) ||
    isHardStopStage(stageStatus)
  );
};

export const isStageConsideredAsCompleted = (stageStatus?: StageStatusEnum) => {
  return isStageCompleted(stageStatus) || isSoftStopHalfCompleted(stageStatus);
};

export const isMemberResponsible = (responsibleRole?: UserRole) => {
  return responsibleRole === UserRole.Users;
};

const getStepIndex = (
  profileStages: ProfileStageModel[],
  step: ProfileStageModel,
) => {
  return profileStages.findIndex(({ stage }) => step.stage === stage);
};

export const isPreviousStageCompleted = (
  stages: ProfileStageModel[],
  step: ProfileStageModel,
) => {
  const stepIndex = getStepIndex(stages, step);
  const prevStage = stages[stepIndex - 1];
  // If it's first stage - prev stage is considered as completed
  return stepIndex === 0
    ? true
    : isStageConsideredAsCompleted(prevStage.stageStatus);
};

export const arePreviousStagesCompleted = (
  profileStages: ProfileStageModel[],
  step: ProfileStageModel,
) => {
  const stepIndex = getStepIndex(profileStages, step);

  return profileStages
    .slice(0, stepIndex)
    .every(({ stageStatus }) => isStageConsideredAsCompleted(stageStatus));
};

interface DisplayRequestButtonOptions {
  isAdmin?: boolean;
  responsibleRole?: UserRole;
  stageStatus?: StageStatusEnum;
  arePrevStagesCompleted: boolean;
}
export const shouldDisplayRequestButton = (
  options: DisplayRequestButtonOptions,
) => {
  const { isAdmin, responsibleRole, stageStatus, arePrevStagesCompleted } =
    options;

  if (!isAdmin || !arePrevStagesCompleted) {
    return false;
  }

  switch (stageStatus) {
    case StageStatusEnum.HardStop:
      return responsibleRole !== UserRole.Users;

    case StageStatusEnum.SoftStopHalfCompleted:
      return false;

    default:
      return true;
  }
};

interface DisplayResponseButtonOptions {
  isAdmin?: boolean;
  requestMessage?: string;
  responseMessage?: string;
}
export const shouldDisplayResponseButton = (
  options: DisplayResponseButtonOptions,
) => {
  const { isAdmin, responseMessage, requestMessage } = options;

  // Should be displayed for member if admin provided the request
  // but member hasn't replied to it yet
  return !isAdmin && !!requestMessage && !responseMessage;
};

export const getProfileProgress = (stages?: ProfileStageModel[]) => {
  if (!stages?.length) return 0;

  // The progress depends on completed stages in a row
  const stageStatuses = stages.map(({ stageStatus }) => stageStatus);
  const firstUncompletedStageStatusIndex = stageStatuses.findIndex(
    (stageStatus) => !isStageConsideredAsCompleted(stageStatus),
  );
  const completedStagesCount = stageStatuses.slice(
    0,
    firstUncompletedStageStatusIndex === -1
      ? stageStatuses.length
      : firstUncompletedStageStatusIndex,
  ).length;

  return Math.round((completedStagesCount / stages.length) * 100);
};

const isNextStepConsideredAsCompleted = (
  stages: ProfileStageModel[],
  stageIndex: number,
) => {
  if (stageIndex === stages.length - 1) return false;

  const nextStep = stages[stageIndex + 1];

  return isStageConsideredAsCompleted(nextStep.stageStatus);
};

export interface StepStatusOptions {
  step: ProfileStageModel;
  stepIndex: number;
  profileStages: ProfileStageModel[];
}
export const getProgressStepStatus = (stepStatusOptions: StepStatusOptions) => {
  const { profileStages, step, stepIndex } = stepStatusOptions;

  if (isStageConsideredAsCompleted(step.stageStatus)) {
    // Do not draw green line if next step is not completed
    return isNextStepConsideredAsCompleted(profileStages, stepIndex)
      ? StepValidationStatuses.Finish
      : StepValidationStatuses.Process;
  }

  return StepValidationStatuses.Wait;
};
